/**
 * Icons for the shape catalogue, drawn from each shape's OWN geometry.
 *
 * A hand-picked lookalike from the icon set stops being possible past the first
 * few shapes (there is no trapezoid or block-arrow glyph), and a wrong icon in a
 * shape menu is genuinely confusing. Generating the outline from the same
 * geometry the canvas uses means every entry — including a 9-sided polygon or a
 * re-tuned arrow — is drawn as exactly what it inserts.
 *
 * Components are cached per shape so React keeps a stable element type.
 */
import { getShapeDef, shapePathD, FREEFORM_SHAPE } from '../schema/shapeLibrary';

const CACHE = new Map();

/** Inset so the stroke isn't clipped by the 24×24 box. */
const PAD = 3;
const SPAN = 24 - PAD * 2;

function glyphBody(def) {
    // A drawn path has no catalogue outline to draw a glyph from — every one of
    // them is different — so the layer list gets a pen-stroke mark instead.
    if (def.key === FREEFORM_SHAPE) {
        return <path d={`M${PAD} 16c3-9 7-9 10-4s6 2 6-4`} />;
    }
    if (def.key === 'ellipse') return <circle cx="12" cy="12" r={SPAN / 2} />;
    if (def.key === 'rect') return <rect x={PAD} y={PAD} width={SPAN} height={SPAN} rx="3" />;
    if (def.key === 'line') return <path d={`M${PAD} 12H${24 - PAD}`} />;
    const el = {
        type: 'shape',
        shape: def.key,
        ...(def.params || {}),
        // Keep the shape's own rounding, scaled down to icon size.
        styles: { borderRadius: def.radius ? 2 : 0 },
    };
    return <path d={shapePathD(el, SPAN, SPAN, PAD, PAD)} />;
}

/** Icon component for a shape key — same props as a Tabler icon. */
export function shapeGlyphIcon(shape) {
    const def = getShapeDef(shape);
    const hit = CACHE.get(def.key);
    if (hit) return hit;

    const body = glyphBody(def);
    const Glyph = ({ size = 16, stroke = 1.75, className, ...rest }) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinejoin="round"
            strokeLinecap="round"
            className={className}
            aria-hidden="true"
            {...rest}
        >
            {body}
        </svg>
    );
    Glyph.displayName = `ShapeGlyph(${def.key})`;
    CACHE.set(def.key, Glyph);
    return Glyph;
}
